import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootReducerState } from "../store/IRootReducer";
import {
  setAccessToken,
  setIdTokenClaims,
  setTokens,
  clearTokens,
  setPermissions,
  setIsPlatformAdmin,
  setIsTenantAdmin,
  setIsInstanceAdmin,
  setIsError,
  setInitialized,
  signOut as signOutAction,
  tenantChange as tenantChangeAction,
  instanceChange as instanceChangeAction,
  IdTokenClaims,
  UserPermissions,
} from "../store/slices/authSlice";

export const useAuth = () => {
  const dispatch = useDispatch();

  const authState = useSelector((state: IRootReducerState) => state.authReducer);

  const accessToken = authState.accessToken;
  const idTokenClaims = authState.idTokenClaims;
  const permissions = authState.permissions;
  const isError = authState.error.isError;
  const initialized = authState.initialized;

  const isPlatformAdmin = permissions.isPlatformAdmin;
  const isTenantAdmin = permissions.isTenantAdmin;
  const isInstanceAdmin = permissions.isInstanceAdmin;

  const updateAccessToken = useCallback(
    (token: string | undefined) => {
      dispatch(setAccessToken(token));
    },
    [dispatch],
  );

  const updateIdTokenClaims = useCallback(
    (claims: IdTokenClaims | undefined) => {
      dispatch(setIdTokenClaims(claims));
    },
    [dispatch],
  );

  const updateTokens = useCallback(
    (tokens: { accessToken: string | undefined; idTokenClaims: IdTokenClaims | undefined }) => {
      dispatch(setTokens(tokens));
    },
    [dispatch],
  );

  const clearAuthTokens = useCallback(() => {
    dispatch(clearTokens());
  }, [dispatch]);

  const updatePermissions = useCallback(
    (perms: UserPermissions) => {
      dispatch(setPermissions(perms));
    },
    [dispatch],
  );

  const updateIsPlatformAdmin = useCallback(
    (value: boolean) => {
      dispatch(setIsPlatformAdmin(value));
    },
    [dispatch],
  );

  const updateIsTenantAdmin = useCallback(
    (value: boolean) => {
      dispatch(setIsTenantAdmin(value));
    },
    [dispatch],
  );

  const updateIsInstanceAdmin = useCallback(
    (value: boolean) => {
      dispatch(setIsInstanceAdmin(value));
    },
    [dispatch],
  );

  const updateIsError = useCallback(
    (value: boolean) => {
      dispatch(setIsError(value));
    },
    [dispatch],
  );

  const updateInitialized = useCallback(
    (value: boolean) => {
      dispatch(setInitialized(value));
    },
    [dispatch],
  );

  const signOut = useCallback(() => {
    dispatch(signOutAction());
  }, [dispatch]);

  const triggerTenantChange = useCallback(() => {
    dispatch(tenantChangeAction());
  }, [dispatch]);

  const triggerInstanceChange = useCallback(() => {
    dispatch(instanceChangeAction());
  }, [dispatch]);

  return {
    accessToken,
    idTokenClaims,
    permissions,
    isPlatformAdmin,
    isTenantAdmin,
    isInstanceAdmin,
    isError,
    initialized,
    updateAccessToken,
    updateIdTokenClaims,
    updateTokens,
    clearAuthTokens,
    updatePermissions,
    updateIsPlatformAdmin,
    updateIsTenantAdmin,
    updateIsInstanceAdmin,
    updateIsError,
    updateInitialized,
    signOut,
    triggerTenantChange,
    triggerInstanceChange,
  };
};

export const useAuthState = () => {
  return useSelector((state: IRootReducerState) => state.authReducer);
};

export const useAccessToken = () => {
  return useSelector((state: IRootReducerState) => state.authReducer.accessToken);
};

export const usePermissions = () => {
  return useSelector((state: IRootReducerState) => state.authReducer.permissions);
};

export const useIsError = () => {
  return useSelector((state: IRootReducerState) => state.authReducer.error.isError);
};

export const useAuthPermissions = () => {
  const authState = useSelector((state: IRootReducerState) => state.authReducer);

  return {
    isPlatformAdmin: authState.permissions.isPlatformAdmin,
    isTenantAdmin: authState.permissions.isTenantAdmin,
    isInstanceAdmin: authState.permissions.isInstanceAdmin,
    isFullAccess: authState.isFullAccess,
    allowView: authState.capabilities.allowView,
    allowEdit: authState.capabilities.allowEdit,
    allowDelete: authState.capabilities.allowDelete,
  };
};

export const useCapabilities = () => {
  return useSelector((state: IRootReducerState) => state.authReducer.capabilities);
};

export const useIsFullAccess = () => {
  return useSelector((state: IRootReducerState) => state.authReducer.isFullAccess);
};
